import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { ApiRoute, FeatureFlag } from 'config';
import { get, post } from 'utils/network';
import { getQueryParam } from 'utils/location';

import Plan, { PlanType } from 'models/Plan';
import MealTypes from 'models/MealTypes';
import IPlanInfo from 'models/IPlanInfo';

import useAsyncEffect from 'hooks/useAsyncEffect';
import useCurrentUser from 'hooks/useCurrentUser';
import useUpcomingMenus from 'hooks/useUpcomingMenus';

import SkeletonLine from 'components/shared/skeletons/SkeletonLine';
import SelectPlanModalContainer from '../modals/SelectPlanModalContainer';
import UpdatePlan from '../../UpdatePlan';
import AddExtrasModal from '../modals/AddExtrasModal';
import PlanConfirmationModal from '../modals/PlanConfirmationModal';

type Props = { className?: string; };

function PlanCard({ className }: Props): ReactElement {
  const { currentUser } = useCurrentUser();
  const [plan, setPlan] = useState(Plan.getPlan());
  const [userPlan, setUserPlan] = useState(Plan.getPlan());
  const [isPlanInitialized, setPlanInitialized] = useState(Plan.isInitialized);
  const [showPlanModal, setShowPlanModal] = useState(getQueryParam('edit') === 'plan');
  const [showExtrasModal, setShowExtrasModal] = useState(getQueryParam('edit') === 'extras');
  const [showPlanConfirmation, setShowPlanConfirmation] = useState(false);
  const {
    isEmpty,
    subscription,
    canEditPlan,
  } = currentUser;
  const isDiversifiedPlansEnabled = currentUser.hasFeatureEnabled(FeatureFlag.DIVERSIFIED_PLANS);

  useAsyncEffect(async (isMounted) => {
    if (!Plan.isInitialized) {
      const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);
      // @ts-expect-error TODO: fix null safety
      Plan.initialize(response.parsedBody);
      if (isMounted) setPlanInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!isEmpty) {
      const upstreamPlan = new Plan(subscription.plan, subscription.extras,
        subscription.is_diversified);
      setPlan(upstreamPlan);
      setUserPlan(upstreamPlan);
    }
  }, [isEmpty, subscription?.plan, subscription?.extras, subscription?.is_diversified]);

  const mealAndQty = (meal, quantity) => (
    quantity > 0
      ? `${quantity}x ${MealTypes.pluralize(meal, quantity)}`
      : ''
  );

  const extras = () => (
    Object.keys(userPlan.selectedExtras).reduce((acc, cur) => (
      `${acc} ${mealAndQty(cur, userPlan.selectedExtras[cur])}`
    ), '').trim()
  );

  // Preload menus. It's required for plan change
  // functionality in the SelectPlanModal.
  useUpcomingMenus();

  const { reload: reloadUser } = useCurrentUser();
  const handlePlanChange = async (newPlan?: Plan) => {
    await post(`${ApiRoute.USERS}/me/subscription`, {
      subscription: {
        plan: newPlan ? newPlan.name : plan.name,
        extra_meals: newPlan ? newPlan.selectedExtras : plan.selectedExtras,
      },
    });

    reloadUser();
  };

  return (
    <div className={`c-card c-profile__card col-12 col-lg-4 ${className}`}>
      <div className="c-card__content">
        <h2 className="c-profile__card-heading">
          Plan

          {canEditPlan && (
          <button
            type="button"
            className="c-link u-text--sm"
            aria-label="Edit plan"
            onClick={() => setShowPlanModal(true)}
          >
            Edit
          </button>
          )}
        </h2>

        <h3 className="c-profile__label">Servings</h3>

        <span>
          {isEmpty ? (
            <SkeletonLine width={180} height={14} />
          ) : (
            `${userPlan.title()} (${userPlan.planCardServings})`
          )}
        </span>

        {!isEmpty && userPlan.servings === 2 && (
        <>
          <h3 className="c-profile__label">Meal Preferences</h3>

          <span>
            {userPlan.mealPreferences}
          </span>
        </>
        )}

        { userPlan.type !== PlanType.PROGRAMS && (
        <>
          <h3 className="c-profile__label">Extras</h3>

          <span>
            {isEmpty && (
            <SkeletonLine width={220} height={14} />
            )}

            {!isEmpty && (
              extras() || 'Upgrade your delivery! Add Extras anytime.'
            )}
          </span>
          <button
            type="button"
            className="c-link u-text--sm c-profile__edit-extras"
            aria-label="Edit extras"
            onClick={() => setShowExtrasModal(true)}
          >
            Edit
          </button>
        </>
        )}

      </div>

      {!isEmpty && isPlanInitialized && (
      <>
        {isDiversifiedPlansEnabled ? (
          <UpdatePlan
            show={showPlanModal}
            existingPlan={plan}
            onClose={() => setShowPlanModal(false)}
          />
        ) : (
          <SelectPlanModalContainer
            show={showPlanModal}
            plan={plan}
            setPlan={setPlan}
            onClose={() => setShowPlanModal(false)}
            loggedIn
            renderFollowUps
          />
        )}

        <AddExtrasModal
          show={showExtrasModal}
          plan={plan}
          setPlan={setPlan}
          onClose={() => setShowExtrasModal(false)}
          onConfirm={async () => {
            await handlePlanChange();
            setShowExtrasModal(false);
            setShowPlanConfirmation(true);
          }}
        />
      </>
      )}
      {showPlanConfirmation && (
      <PlanConfirmationModal
        plan={plan}
        onClose={() => setShowPlanConfirmation(false)}
      />
      )}
    </div>
  );
}

export default PlanCard;
